import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/lib/content";
import CTASection from "@/components/CTASection";
import useSEO from "@/lib/useSEO";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  useSEO({
    title: service ? `${service.title} Lawyer for Creators — Upload Counsel` : "Service — Upload Counsel",
    description: service ? service.description : undefined,
    canonical: service ? `https://uploadlawyer.com/services/${service.slug}` : undefined,
  });
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`page-service-${slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 pt-20 md:pt-28 pb-16">
          <Link to="/services" data-testid="back-to-services" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#D9B36C]">
            <ChevronLeft size={14} /> Back to services
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">{service.title}</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight">
            {service.headline}
          </h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-3xl">
            {service.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/start" data-testid="service-detail-cta" className="inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-7 py-3.5 rounded-full transition-all">
              Start a request <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="relative py-12 md:py-20 border-t border-[#272B36]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">What we cover</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white tracking-tight">Scope of work</h2>
            <ul className="mt-8 space-y-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex gap-3 text-slate-300">
                  <CheckCircle2 size={18} className="text-[#D9B36C] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Common situations</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white tracking-tight">If this sounds familiar</h2>
            <div className="mt-8 space-y-4">
              {service.pain_points.map((p) => (
                <div key={p} className="rounded-2xl border border-[#272B36] bg-[#121419] p-5">
                  <p className="text-slate-200 leading-relaxed">"{p}"</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-6">
              <p className="font-display font-semibold text-white">Triage first. Engage second.</p>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                A licensed attorney reviews your request and tells you whether you need counsel, and which attorney in our network fits. Flat fees where possible.
              </p>
              <Link to="/start" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#D9B36C] hover:gap-2 transition-all">
                Start triage <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 border-t border-[#272B36]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Related surfaces</p>
          <h2 className="mt-3 font-display text-3xl font-bold text-white tracking-tight">Other services</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {others.slice(0, 3).map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`related-service-${o.slug}`} className="beam-card group rounded-2xl border border-[#272B36] bg-[#121419] p-6 hover:border-[#D9B36C]/40 transition-colors">
                <p className="font-display font-semibold text-white">{o.title}</p>
                <p className="mt-1 text-sm text-slate-400">{o.short}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#D9B36C] group-hover:gap-2 transition-all">
                  View <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title={`Ready to triage your ${service.title.toLowerCase()} matter?`}
        body="A 30-minute call. Clear next steps. No commitment to engage until you see scope and price."
      />
    </div>
  );
}
